
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { identifyUser } from "@/utils/logRocketUtils";

export const useAuthService = () => {
  const { toast } = useToast();

  const signIn = async (email: string, password: string) => {
    try {
      // Validation basique des entrées
      if (!email || !email.includes('@') || !password || password.length < 6) {
        throw new Error("Email ou mot de passe invalide");
      }

      const sanitizedEmail = email.trim().toLowerCase();
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: sanitizedEmail,
        password,
      });

      if (error) throw error;
      
      // Identifier l'utilisateur dans LogRocket après connexion
      if (data.user) {
        identifyUser({
          id: data.user.id,
          email: data.user.email,
          name: data.user.user_metadata?.full_name
        });
      }

      toast({
        title: "Connexion réussie",
        description: "Bienvenue sur AlunaTools !",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: error.message,
      });
      throw error;
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      // Validation des entrées
      if (!email || !email.includes('@')) {
        throw new Error("Email invalide");
      }
      if (!password || password.length < 6) {
        throw new Error("Le mot de passe doit contenir au moins 6 caractères");
      }
      if (!fullName || fullName.trim().length < 2) {
        throw new Error("Nom invalide");
      }

      const sanitizedEmail = email.trim().toLowerCase();
      const sanitizedFullName = fullName.trim();

      const { error } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password,
        options: {
          data: {
            full_name: sanitizedFullName,
          },
        },
      });

      if (error) throw error;

      toast({
        title: "Inscription réussie",
        description: "Bienvenue sur AlunaTools !",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur d'inscription",
        description: error.message,
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Déconnexion réussie",
        description: "À bientôt !",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur de déconnexion",
        description: error.message,
      });
      throw error;
    }
  };

  const resetPassword = async (password: string) => {
    try {
      if (!password || password.length < 6) {
        throw new Error("Le mot de passe doit contenir au moins 6 caractères");
      }

      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;
      
      toast({
        title: "Mot de passe mis à jour",
        description: "Votre mot de passe a été mis à jour avec succès.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur de mise à jour",
        description: error.message || "Une erreur s'est produite lors de la mise à jour du mot de passe",
      });
      throw error;
    }
  };

  return {
    signIn,
    signUp,
    signOut,
    resetPassword
  };
};
